import { useTranslation } from "react-i18next"
import { Flex } from "../../../../components/flex/Flex"
import { FormattedUnit } from "../../../../components/FormattedUnit"
import ChartPlaceholder from "../../../../components/placeholder/components/ChartPlaceholder"
import Separator from "../../../../components/Separator"
import { Spacer } from "../../../../components/spacer/Spacer"
import Text from "../../../../components/Text"
import ToggleButton from "../../../../components/toggle/ToggleButton"
import { useIsMaxMd, useIsMaxSm } from "../../../../hooks/mediaHooks"
import { getCurrency } from "../../../../i18n/config"
import { theme } from "../../../../styles/stitches.config"
import { CalculationLabel } from "../CalculationLabel"
import ChartLegend from "../../../../components/chart/ChartLegend"
import { ConfigContainer, FooterContainer, HeaderContainer, PageContainer } from "../containers"
import { DistributionTariffSelect } from "../DistributionTariffSelect"
import { InputSetContainer } from "../input/InputSetContainer"
import { LabelWithTooltip } from "../input/LabelWithTooltip"
import { PowerAndProductionInput } from "../input/PowerAndProductionInput"
import { OptimizationSlider } from "../OptimizationSlider"
import { ValueInput } from "../ValueInput"
import { ProductionAndConsumptionChartResponsive } from "./ProductionAndConsumptionChart"
import { SCard, SOptimizationSummaryItem, SSwitchAndLabelContainer } from "./ProductionAndConsumption.styled"
import { useProductionAndConsumptionController } from "./useProductionAndConsumptionController"
import { ChartLegendContainer } from "../ChartLegendContainer"
import { EnergyUnit } from "@nano-portal/shared"

const ConsumptionInput = ({ value, onChange }: { value?: number; onChange?: (value?: number) => void }) => {
  const { t } = useTranslation("microsite")

  return (
    <ValueInput
      value={value}
      onChange={onChange}
      inputLabel={<LabelWithTooltip label={t("annual_consumption")} tooltip={t("annual_consumption_tooltip")} />}
      valueLabel={EnergyUnit.MWh}
    />
  )
}

interface OptimizationSummaryProps {
  consumption: number
  production: number
  settlement: number
}

const OptimizationSummary = ({ consumption, production, settlement }: OptimizationSummaryProps) => {
  const isSmall = useIsMaxSm()
  const { t } = useTranslation("microsite")

  return (
    <SCard>
      <SOptimizationSummaryItem textAlignOnSm="start">
        <Text type="textsLarge">{t("production_and_consumption_your_consumption")}</Text>
        <Spacer size={theme.space.s2} />
        <FormattedUnit
          value={consumption}
          valueTextType="headerH2Positive"
          unit={getCurrency().symbol}
          unitTextType="headerH5Positive"
          maxFractionDigits={0}
        />
      </SOptimizationSummaryItem>

      {!isSmall && <Separator orientation="vertical" />}

      <SOptimizationSummaryItem textAlignOnSm="end">
        <Text type="textsLarge">{t("production_and_consumption_your_production")}</Text>
        <Spacer size={theme.space.s2} />
        <FormattedUnit
          value={production}
          valueTextType="headerH2Neutral"
          valueColor="warning"
          unit={getCurrency().symbol}
          unitTextType="headerH5Neutral"
          unitColor="warning"
          maxFractionDigits={0}
        />
      </SOptimizationSummaryItem>

      {!isSmall && <Separator orientation="vertical" />}

      <SOptimizationSummaryItem textAlignOnSm="end">
        <Text type="textsLarge">{t("production_and_consumption_settlement")}</Text>
        <Spacer size={theme.space.s2} />
        <FormattedUnit
          value={settlement}
          valueTextType={settlement > 0 ? "headerH2Positive" : "headerH2Negative"}
          unit={getCurrency().symbol}
          unitTextType={settlement > 0 ? "headerH5Positive" : "headerH5Negative"}
          maxFractionDigits={0}
        />
      </SOptimizationSummaryItem>
    </SCard>
  )
}

export const ProductionAndConsumption = () => {
  const controller = useProductionAndConsumptionController()
  const isMedium = useIsMaxMd()
  const isSmall = useIsMaxSm()

  return (
    <PageContainer>
      <HeaderContainer>
        <ConfigContainer>
          <Flex direction={isMedium ? "column" : "row"}>
            <PowerAndProductionInput
              installedPowerInKWp={controller.installedPower}
              onChange={controller.setInstalledPower}
            />

            <Spacer size={theme.space.s6} />

            <InputSetContainer>
              <ConsumptionInput value={controller.annualConsumption} onChange={controller.setAnnualConsumption} />

              <Spacer size={theme.space.s6} />

              <DistributionTariffSelect
                hidePublicLighting
                value={controller.distributionTariff}
                onValueChange={controller.setDistributionTariff}
              />
            </InputSetContainer>
          </Flex>

          <Spacer size={theme.space.s6} />
        </ConfigContainer>

        <SSwitchAndLabelContainer>
          <ToggleButton
            items={controller.toggleButtonItems}
            activeItemValue={controller.group}
            onValueChange={controller.onGroupChange}
          />

          <CalculationLabel />
        </SSwitchAndLabelContainer>

        <Spacer size={theme.space.s6} />
      </HeaderContainer>

      <Spacer size={theme.space.s4} />

      {controller.isLoading ? (
        <ChartPlaceholder />
      ) : (
        <>
          <ProductionAndConsumptionChartResponsive
            isErrorState={controller.isError}
            data={controller.chartData}
            filter={controller.filter}
            group={controller.group}
          />

          <ChartLegendContainer>
            <ChartLegend items={controller.legendItems} />
          </ChartLegendContainer>

          <Spacer size={theme.space.s10} />

          <FooterContainer>
            <OptimizationSlider value={controller.optimization} onValueChange={controller.setOptimization} />

            {isSmall && <Spacer size={theme.space.s6} />}

            <OptimizationSummary
              consumption={controller.totalConsumption}
              production={controller.totalProduction}
              settlement={controller.settlement}
            />
          </FooterContainer>
        </>
      )}
    </PageContainer>
  )
}
